import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { Component, computed, effect, inject, linkedSignal, PLATFORM_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isActive, Router, RouterModule } from '@angular/router';
import { map } from 'rxjs/operators';

import { PendingRequestsService } from '@app/core/progress/pending-requests.service';
import { permissionsOfRoute } from '@app/core/session/route-permission.util';
import { SessionStore } from '@app/core/session/session-store';
import { ActionUserComponent } from '@app/core/shell/action-user.component';

import type { ShellHomeRoute, ShellNavEntry, ShellNavGroup, ShellNavLink } from './shell.types';

const HOME_ROUTE: ShellHomeRoute = '/home';

const NAV_ENTRIES: readonly ShellNavEntry[] = [
  { kind: 'link', icon: 'home', label: 'Início', route: HOME_ROUTE },
  { kind: 'link', icon: 'group', label: 'Leads', route: '/leads' },
  { kind: 'link', icon: 'domain', label: 'Empresas', route: '/companies' },
  {
    kind: 'group',
    id: 'shell-nav-group-metadata',
    icon: 'dataset',
    label: 'Metadados',
    children: [
      { icon: 'contact_page', label: 'Leads', route: '/metadata/lead' },
      { icon: 'business_center', label: 'Empresas', route: '/metadata/company' },
    ],
  },
  { kind: 'link', icon: 'view_kanban', label: 'Oportunidades', route: '/opportunities' },
  { kind: 'link', icon: 'bar_chart', label: 'Relatórios', route: '/report' },
  {
    kind: 'group',
    id: 'shell-nav-group-access',
    icon: 'admin_panel_settings',
    label: 'Acesso',
    children: [
      { icon: 'manage_accounts', label: 'Usuários', route: '/users' },
      { icon: 'badge', label: 'Papéis', route: '/roles' },
    ],
  },
];

const ACTIVE_ROUTE_MATCH = { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' } as const;

const LOADING_ANNOUNCEMENT = 'Carregando';
const LOADED_ANNOUNCEMENT = 'Carregado';
const COLLAPSED_STORAGE_KEY = 'leadgen.sidenav.collapsed';
const MOBILE_BREAKPOINT = '(max-width: 767.98px)';

@Component({
  selector: 'app-shell',
  imports: [
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressBarModule,
    MatTooltipModule,
    NgOptimizedImage,
    ActionUserComponent,
  ],
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent {
  private readonly sessionStore = inject(SessionStore);
  private readonly router = inject(Router);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly announcer = inject(LiveAnnouncer);

  protected readonly hasPending = inject(PendingRequestsService).hasPending;

  private lastAnnouncedPending = false;

  private readonly loadingAnnouncement = effect(() => {
    const pending = this.hasPending();

    if (pending === this.lastAnnouncedPending || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.lastAnnouncedPending = pending;
    void this.announcer.announce(pending ? LOADING_ANNOUNCEMENT : LOADED_ANNOUNCEMENT, 'polite');
  });

  protected readonly isMobile = toSignal(this.breakpoints.observe(MOBILE_BREAKPOINT).pipe(map(state => state.matches)), { initialValue: false });

  protected readonly collapsed = signal(this.readStoredCollapsed());
  protected readonly railMode = computed(() => !this.isMobile() && this.collapsed());
  protected readonly drawerOpened = signal(false);

  protected readonly navEntries = computed(() => NAV_ENTRIES.flatMap(entry => this.visibleEntry(entry)));

  private readonly childRouteActive = new Map(
    NAV_ENTRIES.flatMap(entry => (entry.kind === 'group' ? entry.children : [])).map(child => [child.route, isActive(child.route, this.router, ACTIVE_ROUTE_MATCH)] as const),
  );

  protected readonly activeGroupIds = computed<ReadonlySet<string>>(() => new Set(this.navEntries().flatMap(entry => (this.isActiveGroup(entry) ? [entry.id] : []))), {
    equal: (first, second) => this.hasSameGroups(first, second),
  });

  protected readonly expandedGroupIds = linkedSignal<ReadonlySet<string>>(() => this.activeGroupIds());

  protected readonly userName = computed(() => this.sessionStore.session()?.userName ?? '');
  protected readonly businessName = computed(() => this.sessionStore.session()?.businessName ?? '');

  protected readonly userInitials = computed(() => {
    const parts = this.userName().trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';

    return (first + last).toUpperCase() || '•';
  });

  protected toggleCollapsed(): void {
    this.collapsed.update(value => !value);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(this.collapsed()));
    }
  }

  protected toggleGroup(groupId: string): void {
    this.expandedGroupIds.update(expanded => (expanded.has(groupId) ? new Set([...expanded].filter(id => id !== groupId)) : new Set([...expanded, groupId])));
  }

  protected openDrawer(): void {
    this.drawerOpened.set(true);
  }

  protected closeDrawer(): void {
    this.drawerOpened.set(false);
  }

  private visibleEntry(entry: ShellNavEntry): readonly ShellNavEntry[] {
    if (entry.kind === 'link') {
      return this.mayOpen(entry.route) ? [entry] : [];
    }

    const children = entry.children.filter(child => this.mayOpen(child.route));

    return children.length > 0 ? [{ ...entry, children }] : [];
  }

  private mayOpen(route: ShellNavLink['route']): boolean {
    return route === HOME_ROUTE || this.sessionStore.hasEveryPermission(permissionsOfRoute(route));
  }

  private isActiveGroup(entry: ShellNavEntry): entry is ShellNavGroup {
    return entry.kind === 'group' && entry.children.some(child => this.childRouteActive.get(child.route)?.() === true);
  }

  private hasSameGroups(first: ReadonlySet<string>, second: ReadonlySet<string>): boolean {
    return first.size === second.size && [...first].every(id => second.has(id));
  }

  private readStoredCollapsed(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);

    if (stored !== null) {
      return stored === 'true';
    }

    return window.innerWidth < 1280;
  }
}
